import Immutable from 'immutable';
import { createSelector } from 'reselect';
import { selectCurrentPortfolioVisibilityFilter } from '../Portfolio/selectors';
import { currentUserSelector } from '../Users/selectors';
import { selectCurrentCoursePath } from '../location/CurrentCoursePath/selectors';
import { selectType, selectPayload } from '../location/selectors.common';
import { ROUTE_COURSE_PAGE } from '../routeActions';

export { selectCurrentCoursePath };
export const coursesSelector = (state) => state.Courses;

export const selectCourseSummariesByPath = createSelector(
  coursesSelector,
  (courseByPath) => courseByPath.toJS()
);

export const selectCurrentCourseCode = createSelector(
  [selectCurrentCoursePath],
  (currentCoursePath) => {
    if (currentCoursePath) {
      return `/${currentCoursePath}`.split('/courses/')[1];
    } else {
      return null;
    }
  }
);

export const currentCourseSelector = createSelector(
  coursesSelector,
  selectCurrentCoursePath,
  (courses, currentCourse) => {
    if (courses.has(currentCourse)) {
      return courses.get(currentCourse);
    }
    return null;
  }
);

export const currentCourseSelectorJs = createSelector(
  currentCourseSelector,
  (course) => {
    if (course) {
      return course.toJS();
    }
    return null;
  }
);

export const selectCurrentCourseHasLoaded = createSelector(
  coursesSelector,
  selectCurrentCoursePath,
  (courses, currentCoursePath) => {
    return courses.has(currentCoursePath);
  }
);

export const selectCurrentCourseIsOpenCred = createSelector(
  currentCourseSelector,
  (course) => {
    const courseType = course ? course.get('courseType') : null;
    return courseType === 'OPENCRED';
  }
);

export const currentCourseOutcomesSelector = createSelector(
  currentCourseSelector,
  (course) => {
    let outcomes = course ? course.get('outcomes') : null;
    if (outcomes) {
      outcomes = outcomes.filter((outcome) => !outcome.get('isDeleted'));
    }
    return outcomes;
  }
);

export const selectCurrentCourseOutcomesSelectorObject = createSelector(
  currentCourseOutcomesSelector,
  (outcomes) => (outcomes ? outcomes.toJS() : null)
);

export const selectCurrentCourseIsOffline = createSelector(
  currentCourseSelector,
  (course) => {
    return course ? course.get('isOffline') : true;
  }
);

export const selectCurrentCourseSubscriptionId = createSelector(
  currentCourseSelector,
  (course) => (course ? course.get('subscriptionId', null) : null)
);

export const selectCurrentCourseSubscriptionPlanType = createSelector(
  currentCourseSelector,
  (course) => (course ? course.get('subscriptionPlanType', null) : null)
);

export const selectCurrentCourseSubscriptionIsActive = createSelector(
  currentCourseSelector,
  (course) => (course ? course.get('subscriptionIsActive', false) : false)
);

export const selectCurrentCourseLanguage = createSelector(
  currentCourseSelector,
  (course) => (course ? course.get('language', null) : null)
);

export const currentCourseObjectiveSelector = createSelector(
  currentCourseSelector,
  (course) => {
    return course ? course.get('objective') : null;
  }
);

export const selectIsAdminOfCurrentCourse = createSelector(
  currentCourseSelector,
  currentUserSelector,
  (currentCourse, currentUser) => {
    if (!currentCourse || !currentUser) {
      return false;
    }
    return (
      currentCourse.get('admins').indexOf(currentUser.get('profileName')) !== -1
    );
  }
);

export const selectCoursesForPortfolioByVisibilityFilter = createSelector(
  coursesSelector,
  selectCurrentPortfolioVisibilityFilter,
  (courses, visibilityFilter) => {
    let visibility = Immutable.List();

    if (visibilityFilter === 'all') {
      visibility = Immutable.List(['show', 'hide']);
    } else {
      visibility = Immutable.List([visibilityFilter]);
    }

    const hasRightVisibilityFilter = (course) => {
      if (course.get('visibility')) {
        const testVisiblity = visibility.filter((visibilityType) =>
          course.get('visibility').includes(visibilityType)
        );
        return testVisiblity.size > 0;
      } else {
        return false;
      }
    };

    return courses
      .filter((course) => hasRightVisibilityFilter(course))
      .toList();
  }
);

export const isCurrentCourseRTLSelector = createSelector(
  currentCourseSelector,
  (currentCourse) => {
    if (!currentCourse) {
      return false;
    }

    return currentCourse.get('isRightToLeft', false);
  }
);

export const selectIsInCourse = createSelector(
  selectCurrentCoursePath,
  (coursePath) => !!coursePath
);

export const currentCourseInstitutionSelector = createSelector(
  currentCourseSelector,
  (course) => {
    return course ? course.get('institution') : null;
  }
);

export const selectCurrentCourseThemeData = createSelector(
  [currentCourseSelector],
  (course) => (course ? course.get('themeData').toJS() : null)
);

export const selectIsCourseFeedPage = createSelector(
  selectType,
  selectPayload,
  (type, payload) =>
    type === ROUTE_COURSE_PAGE && payload.get('subPath') === 'coursefeed'
);

export const selectCurrentCourseSectorTypes = createSelector(
  [currentCourseSelector],
  (course) =>
    course && course.get('courseSectorTypes')
      ? course.get('courseSectorTypes').toJS()
      : []
);

export const selectCurrentCourseFacialCheckEnabled = createSelector(
  [currentCourseSelector],
  (course) => {
    return course && !!course.get('facialCheckEnabled');
  }
);

export const selectCurrentCourseKeystrokeEnabled = createSelector(
  [currentCourseSelector],
  (course) => {
    return course && !!course.get('keystrokeEnabled');
  }
);
